import { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { CrudMode } from '@ohpen/enso-ui';
import { IconUsers } from '@tabler/icons-react';

import { AdvisersList } from '@pmp/components';
import {
  AdviserCollaboration,
  AssistantPermissionType,
  FieldType,
  firmIntermediariesGetFirmAdvisers,
  FirmIntermediaryByRole,
} from '@pmp/api';
import { CollaborationRow } from '../../advisers/interfaces';

import { CrudFormCard } from '../../firms/components/CrudFormCard';
import { TabProps } from '../interfaces';
import { TabContent } from './TabContent';

export const AdvisersTab: React.FC<TabProps> = ({ form, mode, selectsOptions, setDisableSave }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const [availableAdvisers, setAvailableAdvisers] = useState<FirmIntermediaryByRole[]>([]);
  const [isFetchingAvailableAdvisers, setIsFetchingAvailableAdvisers] = useState(false);
  const [collaborationRows, setCollaborationRows] = useState<CollaborationRow[]>([]);
  const initializedRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const watchedFirmId = Form.useWatch('firmId', form) as string | undefined;
  const watchedIntermediaryId = Form.useWatch('id', { form, preserve: true }) as string | undefined;

  useEffect(() => {
    initializedRef.current = false;
    hasFetchedRef.current = false;
    setCollaborationRows([]);
    if (!watchedFirmId) {
      setAvailableAdvisers([]);
      return;
    }
    let cancelled = false;
    setIsFetchingAvailableAdvisers(true);
    firmIntermediariesGetFirmAdvisers({ path: { firmId: watchedFirmId } })
      .then(({ data }) => { if (!cancelled) setAvailableAdvisers(data ?? []); })
      .catch(() => { })
      .finally(() => { if (!cancelled) { hasFetchedRef.current = true; setIsFetchingAvailableAdvisers(false); } });
    return () => { cancelled = true; };
  }, [watchedFirmId]);

  useEffect(() => {
    if (initializedRef.current || !hasFetchedRef.current) return;
    initializedRef.current = true;
    const existing = (form?.getFieldValue('advisers') as AdviserCollaboration[] | null) ?? [];
    if (existing.length > 0) {
      const rows = existing
        .map(({ adviserId, permissionType, displayName }) => {
          const a = availableAdvisers.find(a => a.id === adviserId);
          return adviserId
            ? { adviserId, name: displayName ?? '', collaborationType: permissionType ?? '', roles: a?.roles, email: a?.email }
            : null;
        })
        .filter(Boolean) as CollaborationRow[];
      setCollaborationRows(rows);
    }
  }, [isFetchingAvailableAdvisers, availableAdvisers, form]);

  const collaborationOptions = useMemo(
    () => (selectsOptions[FieldType.ASSISTANT_PERMISSION_TYPE] ?? []),
    [selectsOptions],
  );

  const handleCollaborationRowsChange = (rows: CollaborationRow[]) => {
    setCollaborationRows(rows);
    form?.setFieldsValue({
      advisers: rows.map(r => ({ adviserId: r.adviserId, permissionType: r.collaborationType as AssistantPermissionType })),
    });
    setDisableSave?.(false);
  };

  return (
    <TabContent noScroll>
      <CrudFormCard collapsible={readonly} loading={isFetchingAvailableAdvisers} icon={<IconUsers />} title={t('lender.brokers.labels.advisers')}>
        <AdvisersList
          readonly={readonly}
          collaborationMode={!readonly}
          availableAdvisers={availableAdvisers}
          currentIntermediaryId={watchedIntermediaryId}
          collaborationRows={collaborationRows}
          onCollaborationRowsChange={handleCollaborationRowsChange}
          roleOptions={selectsOptions[FieldType.USER_ROLE_TYPE]}
          collaborationOptions={collaborationOptions}
        />
      </CrudFormCard>
    </TabContent>
  );
};
